import React, { useState } from 'react';
import { CloudUpload, FileText, X } from 'lucide-react';
import paymentProofService from '../../services/paymentProofService';

const PaymentProofUpload = ({ campaignId, referenceCode, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid file (JPEG, PNG, or PDF)');
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    
    setSelectedFile(file);
    setError(null);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // For PDFs, just show an icon
      setPreviewUrl(null);
    }
  };
  
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      // Use the reference code from props or generate a new one
      const refCode = referenceCode || paymentProofService.generateReferenceCode(campaignId);
      
      await paymentProofService.uploadPaymentProof(campaignId, selectedFile, refCode);
      
      setUploading(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      console.error('Error uploading payment proof:', err);
      setError('Failed to upload payment proof. Please try again.');
      setUploading(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      
      <div className="border-2 border-dashed border-gray-300 rounded-md p-6">
        {!selectedFile ? (
          <div className="text-center">
            <CloudUpload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-2">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-700">
                  Upload payment proof
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  PNG, JPG, JPEG, PDF up to 5MB
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                />
                <span className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                  Select File
                </span>
              </label>
            </div>
          </div>
        ) : (
          <div className="relative">
            <button
              type="button"
              className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-md"
              onClick={handleRemoveFile}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
            
            <div className="flex flex-col items-center">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Payment proof preview"
                  className="h-48 object-contain mb-4"
                />
              ) : (
                <FileText className="h-16 w-16 text-gray-400 mb-4" />
              )}
              
              <p className="text-sm text-gray-500 mb-2">
                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
              
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Payment Proof'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentProofUpload;
